import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableChartIcon from '@mui/icons-material/TableChart';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SaveIcon from '@mui/icons-material/Save';
import XtramModal, { XtramButton, XtramCheckbox, XtramField, XtramInput, XtramSearch } from '../../../Widgets/XtramModal/XtramModal';
import ReportsService from '../../../../Services/reports.service.js';
import { PeriodFields, ReportPreview, ReportTypePicker } from './WithholdingModalParts.jsx';
import styles from './WithholdingModal.module.scss';

const REPORT_TYPES = [
    { value: 'yearlyReport', label: 'AnnualWithholdingTaxReport' },
    { value: 'monthlyReport', label: 'PeriodicWithholdingTaxReport' },
    { value: 'monthlyPeriodReport', label: 'PeriodicReportByMonths' },
];

const ClientWithholdingModal = ({
    open,
    onClose,
    t,
    direction,
    withHoldingTaxType,
    setWithholdingTaxTypeFunc,
    isDateRange,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    date,
    setDate,
    dayjs,
    formattedAccountSourceDeductionCardOptions,
    selectedAccountCard,
    handleSelectedAccountCardChangeForWithholding,
    handleDownloadClientWithholdingReportReport,
    handleDownloadClientWithholdingExcelReport,
    handleShowClientWithholdingReportReport,
    modalStyle: customModalStyle,
    client,
    partners = []
}) => {
    const [htmlContent, setHtmlContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [showZeroBalanceCards, setShowZeroBalanceCards] = useState(false);
    const [originalHtmlContent, setOriginalHtmlContent] = useState('');
    const navigate = useNavigate();

    // Handle showing the report
    const handleShowReport = useCallback(async () => {
        if (!withHoldingTaxType) return;
        
        setIsLoading(true);
        try {
            const html = await handleShowClientWithholdingReportReport(showZeroBalanceCards);
            // Always set originalHtmlContent when fetching fresh data
            setOriginalHtmlContent(html);
            setHtmlContent(html);
            // Reset search filter when new data is loaded
            setClientSearchTerm('');
        } catch (error) {
            console.error('Error showing report:', error);
        } finally {
            setIsLoading(false);
        }
    }, [withHoldingTaxType, showZeroBalanceCards, handleShowClientWithholdingReportReport]);

    // Consolidated save function - SINGLE SOURCE OF TRUTH to prevent duplicate API calls
    const handleSaveReportingData = useCallback(async (data) => {
        const { cardId, caseId, year, amount, withholding, comment } = data;
        
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('שגיאה: לא נמצא טוקן אימות');
                return false;
            }

            const requestData = {
                caseId: caseId,
                accountCardId: cardId,
                year: parseInt(year),
                totalAmount: parseFloat(amount) || 0,
                totalWithholdingTax: parseFloat(withholding) || 0,
                comment: comment
            };

            // Single API call - this is the ONLY place where the API is called
            const result = await ReportsService.saveReportingWithholdingTaxSummaryPerAccount(requestData);

            if (result.success) {
                // Show success message
                toast.success('הנתונים נשמרו בהצלחה!');
                
                // Refresh the report to show updated data with green/red status
                await handleShowReport();
                
                return true;
            } else {
                toast.error('שגיאה בשמירת הנתונים: ' + (result.message || 'שגיאה לא ידועה'));
                return false;
            }
        } catch (error) {
            console.error('Error saving reporting data:', error);
            toast.error('שגיאה בשמירת הנתונים');
            return false;
        }
    }, [handleShowReport]);

    // No longer needed - we handle saves directly from the parent
    // Removed to prevent duplicate API calls

    // Navigation handler for row clicks
    const handleRowClick = useCallback(async (accountCardId, accountCardName, palCode) => {
        
        if (!client || !accountCardId || !accountCardName) {
            return;
        }

        try {
            const navigationState = {
                client,
                accountCardId: accountCardId,
                accountCardName: accountCardName,
                palCodeName: palCode || 'NonPalCode', // Use dynamic palCode or fallback
                startDate,
                endDate,
                isSortCodeReport: true,
                breadcrumbs: ['Reports', 'LedgerReport'],
            };
            
            
            
            // Navigate to AccountCardDashboard with the data
            navigate('/AccountCardDashboard', {
                state: navigationState,
            });
        } catch (error) {
            console.error('Error navigating to AccountCardDashboard:', error);
        }
    }, [client, startDate, endDate, navigate]);

    // Handle manual reporting data save
    const handleSaveManualReportingData = useCallback(async () => {
        if (!selectedAccountCard || !client) {
            console.error('Missing required data for manual reporting save');
            toast.warning('אנא בחר כרטיס חשבון לפני שמירת הנתונים');
            return;
        }

        const amountElement = document.getElementById('manualAmount');
        const withholdingElement = document.getElementById('manualWithholding');
        const commentElement = document.getElementById('manualComment');

        if (!amountElement || !withholdingElement || !commentElement) {
            console.error('Form elements not found');
            toast.error('שגיאה בטעינת הטופס');
            return;
        }

        const amount = parseFloat(amountElement.value) || 0;
        const withholding = parseFloat(withholdingElement.value) || 0;
        const comment = commentElement.value || '';

        // Use the consolidated save function to prevent duplicate API calls
        const saveData = {
            cardId: selectedAccountCard.value,
            caseId: client.caseId,
            year: date ? date.year() : new Date().getFullYear(),
            amount: amount,
            withholding: withholding,
            comment: comment
        };

        await handleSaveReportingData(saveData);

        // Clear the form after successful save
        if (amountElement) amountElement.value = '';
        if (withholdingElement) withholdingElement.value = '';
        if (commentElement) commentElement.value = '';
    }, [selectedAccountCard, client, date, handleSaveReportingData]);

    // Filter function to apply search and zero balance filters
    const applyFilters = useCallback(() => {
        if (!originalHtmlContent) return;

        // Create a temporary DOM element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = originalHtmlContent;

        // Check if this is a multi-table report (monthly report with showMode=true)
        const allTables = tempDiv.querySelectorAll('table');
        const isMultiTableReport = allTables.length > 1;

        if (isMultiTableReport) {
            // Handle multi-table reports (monthly with showMode=true)
            
            // Process each table separately
            allTables.forEach((table, tableIndex) => {
                const tbody = table.querySelector('tbody');
                if (!tbody) return;

                // Get all rows from this table
                const allRows = tbody.querySelectorAll('tr');
        const dataRows = Array.from(allRows).filter(row => {
            const firstCell = row.querySelector('td:first-child');
                    return firstCell && 
                           !firstCell.textContent.includes('סה״כ בחודש') && 
                           !firstCell.textContent.includes('סה״כ בדוח') &&
                           !firstCell.textContent.includes('מספר לקוחות');
                });

                let filteredRows = Array.from(dataRows);

                // Apply both client search filter and zero balance filter together
                filteredRows = filteredRows.filter(row => {
                    // Apply client search filter
                    if (clientSearchTerm.trim()) {
                        const searchTerm = clientSearchTerm.trim();
                        const clientNameCell = row.querySelector('td:first-child');
                        if (clientNameCell) {
                            const clientName = clientNameCell.textContent;
                            if (!clientName.includes(searchTerm)) {
                                return false; // Skip this row if it doesn't match search
                            }
                        }
                    }

                    // Apply zero balance filter
                    if (!showZeroBalanceCards) {
                const cells = row.querySelectorAll('td');
                        if (cells.length === 3) {
                            // 3-column structure: Client Name (0), Total Receipts (1), Withholding Tax (2)
                            const totalBalanceText = cells[1]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[2]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        } else if (cells.length === 5) {
                            // 5-column structure: Client Name (0), Document Type (1), Number (2), Total Receipts (3), Withholding Tax (4)
                            const totalBalanceText = cells[3]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[4]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        } else if (cells.length === 8) {
                            // 8-column structure: Client Name (0), Document Type (1), Number (2), Total Receipts (3), Withholding Tax (4), Reporting Receipts (5), Reporting Withholding (6), Comment (7)
                            const totalBalanceText = cells[3]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[4]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        }
                        return true;
                    }
                    
                    return true; // If showZeroBalanceCards is true, include all rows
                });

                // Clear existing rows and add filtered rows
                tbody.innerHTML = '';
                filteredRows.forEach(row => {
                    const clonedRow = row.cloneNode(true);
                    tbody.appendChild(clonedRow);
                    
                    // Also include the corresponding details row if it exists
                    const expandButton = clonedRow.querySelector('.btn-expand');
                    if (expandButton) {
                        const uniqueId = expandButton.getAttribute('data-unique-id');
                        if (uniqueId) {
                            // Always look in the original HTML content, not the filtered tempDiv
                            const originalDetailsRow = document.createElement('div');
                            originalDetailsRow.innerHTML = originalHtmlContent;
                            const detailsRow = originalDetailsRow.querySelector(`#details_${uniqueId}`);
                            if (detailsRow) {
                                const clonedDetailsRow = detailsRow.cloneNode(true);
                                tbody.appendChild(clonedDetailsRow);
                            }
                        }
                    }
                });

                // Add monthly subtotal if this is not the last table (final totals table)
                if (tableIndex < allTables.length - 1) {
                    const monthName = table.previousElementSibling?.querySelector('p')?.textContent?.trim() || '';
                    if (monthName) {
                        // Calculate totals for this month
                        let monthBalance = 0;
                        let monthWithholding = 0;
                        
                        filteredRows.forEach(row => {
                            const cells = row.querySelectorAll('td');
                            if (cells.length === 3) {
                                // Old 3-column structure
                                const balanceText = cells[1]?.textContent?.trim() || '0';
                                const withholdingText = cells[2]?.textContent?.trim() || '0';
                                
                                const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                
                                const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                
                                const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                
                                monthBalance += balance;
                                monthWithholding += withholding;
                            } else if (cells.length === 5) {
                                // Old 5-column structure
                                const balanceText = cells[3]?.textContent?.trim() || '0';
                                const withholdingText = cells[4]?.textContent?.trim() || '0';
                                
                                const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                
                                const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                
                                const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                
                                monthBalance += balance;
                                monthWithholding += withholding;
                            } else if (cells.length === 8) {
                                // 8-column structure with reporting columns
                                const balanceText = cells[3]?.textContent?.trim() || '0';
                                const withholdingText = cells[4]?.textContent?.trim() || '0';
                                
                                const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                
                                const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                
                                const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                
                                monthBalance += balance;
                                monthWithholding += withholding;
                            }
                        });
                        if (clientSearchTerm.trim())  {
                        // Add monthly subtotal
                        const subtotalRow = document.createElement('tr');
                        subtotalRow.style.borderTop = '1px solid black';
                        subtotalRow.innerHTML = `
                            <td style="font-weight: bold;">סה״כ בחודש ${monthName}:</td>
                            <td style="font-weight: bold;"></td>
                            <td style="font-weight: bold;"></td>
                            <td style="font-weight: bold;">${monthBalance.toLocaleString('he-IL')}</td>
                            <td style="font-weight: bold;">${monthWithholding.toLocaleString('he-IL')}</td>
                            <td style="font-weight: bold;"></td>
                            <td style="font-weight: bold;"></td>
                            <td style="font-weight: bold;"></td>
                        `;
                        tbody.appendChild(subtotalRow);
                        }
                    }
                } else {
                    // This is the final totals table - calculate overall totals
                    let totalBalance = 0;
                    let totalWithholding = 0;
                    let clientCount = 0;

                    // Calculate totals from all previous tables
                    for (let i = 0; i < allTables.length - 1; i++) {
                        const prevTable = allTables[i];
                        const prevTbody = prevTable.querySelector('tbody');
                        if (prevTbody) {
                            const prevRows = prevTbody.querySelectorAll('tr');
                            prevRows.forEach(row => {
                                const cells = row.querySelectorAll('td');
                                if ((cells.length === 3 || cells.length === 5 || cells.length === 8) && !cells[0].textContent.includes('סה״כ בחודש')) {
                                    clientCount++;
                                    
                                    let balanceText, withholdingText;
                                    if (cells.length === 3) {
                                        // Old 3-column structure
                                        balanceText = cells[1]?.textContent?.trim() || '0';
                                        withholdingText = cells[2]?.textContent?.trim() || '0';
                                    } else if (cells.length === 5) {
                                        // 5-column structure
                                        balanceText = cells[3]?.textContent?.trim() || '0';
                                        withholdingText = cells[4]?.textContent?.trim() || '0';
                                    } else if (cells.length === 8) {
                                        // 8-column structure with reporting columns
                                        balanceText = cells[3]?.textContent?.trim() || '0';
                                        withholdingText = cells[4]?.textContent?.trim() || '0';
                                    }
                                    
                                    const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                    const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                    
                                    const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                    const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                    
                                    const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                    const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                    
                                    totalBalance += balance;
                                    totalWithholding += withholding;
                                }
                            });
                        }
                    }
                    // Add final totals only when search is active
                    if (clientSearchTerm.trim() && clientSearchTerm.trim().length > 1) {
                        const totalsRow = document.createElement('tr');
                        totalsRow.style.borderTop = '1px solid black';
                        totalsRow.innerHTML = `
                            <td style="font-weight: bold;">סה״כ בדוח:</td>
                            <td style="font-weight: bold;"></td>
                            <td style="font-weight: bold;"></td>
                            <td style="font-weight: bold;">${totalBalance.toLocaleString('he-IL')}</td>
                            <td style="font-weight: bold;">${totalWithholding.toLocaleString('he-IL')}</td>
                        `;
                        tbody.appendChild(totalsRow);
                    }

                    const clientCountRow = document.createElement('tr');
                    clientCountRow.style.borderTop = '1px solid black';
                    clientCountRow.innerHTML = `
                        <td style="font-weight: bold;">מספר לקוחות עם ניכוי מס: ${clientCount}</td>

                    `;
                    tbody.appendChild(clientCountRow);
                }
            });
        } else {
            // Handle single table reports (yearly reports or monthly with showMode=false)
            const allRows = tempDiv.querySelectorAll('tbody tr');
            const dataRows = Array.from(allRows).filter(row => {
                const firstCell = row.querySelector('td:first-child');
                return firstCell && !firstCell.textContent.includes('סה״כ לקוחות בדוח');
            });
        
        let filteredRows = Array.from(dataRows);

        // Apply both client search filter and zero balance filter together
        filteredRows = filteredRows.filter(row => {
            // Apply client search filter
            if (clientSearchTerm.trim()) {
                const searchTerm = clientSearchTerm.trim();
                const clientNameCell = row.querySelector('td:first-child');
                if (clientNameCell) {
                    const clientName = clientNameCell.textContent;
                    if (!clientName.includes(searchTerm)) {
                        return false; // Skip this row if it doesn't match search
                    }
                }
            }

            // Apply zero balance filter
            if (!showZeroBalanceCards) {
                const cells = row.querySelectorAll('td');
                    
                        if (cells.length === 3) {
                            // Old 3-column monthly report structure: Client Name (0), Total Receipts (1), Withholding Tax (2)
                            const totalBalanceText = cells[1]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[2]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        } else if (cells.length === 5) {
                            // Old 5-column monthly report structure: Client Name (0), Document Type (1), Number (2), Total Receipts (3), Withholding Tax (4)
                            const totalBalanceText = cells[3]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[4]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                                        return totalBalance !== 0 || totalWithholding !== 0;
                        } else if (cells.length === 8) {
                            // 8-column structure with reporting columns: Client Name (0), Document Type (1), Number (2), Total Receipts (3), Withholding Tax (4), Reporting Receipts (5), Reporting Withholding (6), Comment (7)
                            const totalBalanceText = cells[3]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[4]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        }
                return true;
            }
            
            return true; // If showZeroBalanceCards is true, include all rows
        });

        // Rebuild the table with filtered rows
        const tbody = tempDiv.querySelector('tbody');
        if (tbody) {
            tbody.innerHTML = '';
            filteredRows.forEach(row => {
                const clonedRow = row.cloneNode(true);
                tbody.appendChild(clonedRow);
                
                // Also include the corresponding details row if it exists
                const expandButton = clonedRow.querySelector('.btn-expand');
                if (expandButton) {
                    const uniqueId = expandButton.getAttribute('data-unique-id');
                    if (uniqueId) {
                        // Always look in the original HTML content, not the filtered tempDiv
                        const originalDetailsRow = document.createElement('div');
                        originalDetailsRow.innerHTML = originalHtmlContent;
                        const detailsRow = originalDetailsRow.querySelector(`#details_${uniqueId}`);
                        if (detailsRow) {
                            const clonedDetailsRow = detailsRow.cloneNode(true);
                            tbody.appendChild(clonedDetailsRow);
                        }
                    }
                }
            });

            // Calculate totals from filtered data rows only
            let totalBalance = 0;
            let totalWithholding = 0;
            let clientCount = filteredRows.length;

            filteredRows.forEach(row => {
                const cells = row.querySelectorAll('td');
                    
                    if (cells.length === 3) {
                        // Old 3-column monthly report structure
                        const balanceText = cells[1]?.textContent?.trim() || '0';
                        const withholdingText = cells[2]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                        const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                        
                        const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                        
                        const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        totalBalance += balance;
                        totalWithholding += withholding;
                    } else if (cells.length === 5) {
                        // New 5-column monthly report structure
                        const balanceText = cells[3]?.textContent?.trim() || '0';
                        const withholdingText = cells[4]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                        const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                        
                        const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                        
                        const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                         totalBalance += balance;
                         totalWithholding += withholding;
                     } else if (cells.length === 8) {
                         // 8-column structure with reporting columns
                         const balanceText = cells[3]?.textContent?.trim() || '0';
                         const withholdingText = cells[4]?.textContent?.trim() || '0';
                         
                         const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                         const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                         
                         const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                         const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                         
                         const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                         const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                         
                         totalBalance += balance;
                         totalWithholding += withholding;
                }
            });

            // Add totals row
            const totalsRow = document.createElement('tr');
            totalsRow.style.borderTop = '1px solid black';
                
                const firstRowCells = filteredRows.length > 0 ? filteredRows[0].querySelectorAll('td').length : 0;
                
                if (firstRowCells === 3) {
                    // Old 3-column monthly report structure
                    totalsRow.innerHTML = `
                        <td style="font-weight: bold;">סה״כ לקוחות בדוח: ${clientCount}</td>
                        <td style="font-weight: bold;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold;">${totalWithholding.toLocaleString('he-IL')}</td>
                    `;
                } else if (firstRowCells === 5) {
                    // Old 5-column monthly report structure
                    totalsRow.innerHTML = `
                        <td style="font-weight: bold;">סה״כ לקוחות בדוח: ${clientCount}</td>
                        <td style="font-weight: bold;"></td>
                        <td style="font-weight: bold;"></td>
                        <td style="font-weight: bold;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold;">${totalWithholding.toLocaleString('he-IL')}</td>
                    `;
                } else if (firstRowCells === 8) {
                    // 8-column structure with reporting columns
                    totalsRow.innerHTML = `
                        <td style="font-weight: bold;">סה״כ לקוחות בדוח: ${clientCount}</td>
                        <td style="font-weight: bold;"></td>
                        <td style="font-weight: bold;"></td>
                        <td style="font-weight: bold;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold;">${totalWithholding.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold;"></td>
                        <td style="font-weight: bold;"></td>
                        <td style="font-weight: bold;"></td>
                    `;
                } else {
                    // Fallback for unknown structures
                    totalsRow.innerHTML = `
                        <td style="font-weight: bold;">סה״כ לקוחות בדוח: ${clientCount}</td>
                    `;
                }
            tbody.appendChild(totalsRow);
            }
        }

        // Update the displayed content
        setHtmlContent(tempDiv.innerHTML);
    }, [originalHtmlContent, clientSearchTerm, showZeroBalanceCards]);

    // Apply filters when search term or zero balance toggle changes
    useEffect(() => {
        if (originalHtmlContent) {
            // Check if searching for totals/summary rows
            const searchTerm = clientSearchTerm.trim();
            const isSearchingForTotals = searchTerm && (
                searchTerm.includes('סה״כ') || 
                searchTerm.includes('סה"כ') ||
                searchTerm.includes('מספר') ||
                searchTerm.includes('בדוח')
            );
            
            if (isSearchingForTotals) {
                // If searching for totals keywords, show original content (which includes totals)
                setHtmlContent(originalHtmlContent);
            } else if (searchTerm) {
                // Normal search by client/supplier name
                applyFilters();
            } else {
                // If no search term, show original content
                setHtmlContent(originalHtmlContent);
            }
        }
    }, [applyFilters, clientSearchTerm]);

    const handleZeroBalanceChange = async (checked) => {
        setShowZeroBalanceCards(checked);
        if (!withHoldingTaxType || !originalHtmlContent) return;
        setIsLoading(true);
        try {
            const html = await handleShowClientWithholdingReportReport(checked);
            setOriginalHtmlContent(html);
            setHtmlContent(html);
            setClientSearchTerm('');
        } catch (error) {
            console.error('Error updating report with filter:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearFilters = () => {
        setClientSearchTerm('');
        setShowZeroBalanceCards(false);
    };

    const handleFrameLoad = (e) => {
        const iframe = e.target;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        if (!iframeDoc) return;

        iframeDoc.querySelectorAll('tbody tr').forEach((row) => {
            const accountCardId = row.getAttribute('data-id');
            const accountCardName = row.querySelector('td:first-child')?.textContent?.trim();
            const palCode = row.getAttribute('data-type');
            if (!accountCardId || !accountCardName) return;

            row.style.cursor = 'pointer';
            row.style.transition = 'background-color 0.2s ease';

            row.addEventListener('click', (e) => {
                // Clicks on the inline reporting inputs and buttons must not navigate
                if (e.target.classList.contains('btn-expand') ||
                    e.target.classList.contains('btn-save-reporting') ||
                    e.target.tagName === 'INPUT' ||
                    e.target.tagName === 'TEXTAREA' ||
                    e.target.tagName === 'BUTTON' ||
                    e.target.closest('input') ||
                    e.target.closest('textarea') ||
                    e.target.closest('button') ||
                    e.target.closest('.btn-save-reporting') ||
                    e.target.closest('input[id^="amount_"]') ||
                    e.target.closest('input[id^="withholding_"]') ||
                    e.target.closest('input[id^="comment_"]') ||
                    e.target.closest('textarea[id^="comment_"]')) {
                    e.stopPropagation();
                    return;
                }
                handleRowClick(accountCardId, accountCardName, palCode);
            });

            row.addEventListener('mouseenter', () => {
                row.style.backgroundColor = '#f8f9fa';
            });

            row.addEventListener('mouseleave', () => {
                row.style.backgroundColor = '';
            });
        });

        iframeDoc.querySelectorAll('.btn-save-reporting').forEach((button) => {
            button.addEventListener('click', async (e) => {
                e.stopPropagation();

                const cardId = button.getAttribute('data-card-id');
                const caseId = button.getAttribute('data-case-id');
                const year = button.getAttribute('data-year');

                const amountInput = iframeDoc.getElementById(`amount_${cardId}`);
                const withholdingInput = iframeDoc.getElementById(`withholding_${cardId}`);
                const commentInput = iframeDoc.getElementById(`comment_${cardId}`);

                if (!amountInput || !withholdingInput || !commentInput) {
                    console.error('Input elements not found');
                    return;
                }

                await handleSaveReportingData({
                    cardId,
                    caseId,
                    year,
                    amount: amountInput.value,
                    withholding: withholdingInput.value,
                    comment: commentInput.value,
                });

                button.style.display = 'none';
            });

            button.addEventListener('mouseenter', (e) => {
                e.stopPropagation();
                button.style.transform = 'scale(1.05)';
                button.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
            });

            button.addEventListener('mouseleave', (e) => {
                e.stopPropagation();
                button.style.transform = 'scale(1)';
                button.style.boxShadow = '';
            });
        });

        iframeDoc
            .querySelectorAll('input[id^="amount_"], input[id^="withholding_"], input[id^="comment_"], textarea[id^="comment_"]')
            .forEach((input) => {
                input.addEventListener('focus', (e) => e.stopPropagation());
                input.addEventListener('click', (e) => e.stopPropagation());
                input.addEventListener('input', (e) => e.stopPropagation());

                input.addEventListener('mouseenter', (e) => {
                    e.stopPropagation();
                    input.style.backgroundColor = '#fff3cd';
                    input.style.borderColor = '#ffc107';
                });

                input.addEventListener('mouseleave', (e) => {
                    e.stopPropagation();
                    input.style.backgroundColor = '';
                    input.style.borderColor = '#ddd';
                });
            });
    };

    const modalTitle = [t('ClientWithholdingTaxReports'), client?.name].filter(Boolean).join(' · ');
    const showManualReporting = withHoldingTaxType === 'yearlyReport'
        && selectedAccountCard?.value
        && client
        && formattedAccountSourceDeductionCardOptions?.length > 0;

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={modalTitle}
            direction={direction}
            expanded
            footer={
                <>
                    <XtramButton variant="secondary" onClick={onClose}>{t('Close')}</XtramButton>
                    <XtramButton
                        variant="secondary"
                        onClick={() => handleDownloadClientWithholdingExcelReport(showZeroBalanceCards)}
                        disabled={!withHoldingTaxType}
                    >
                        <TableChartIcon fontSize="small" />
                        {t('DownloadExcelFile')}
                    </XtramButton>
                    <XtramButton
                        onClick={() => handleDownloadClientWithholdingReportReport(showZeroBalanceCards)}
                        disabled={!withHoldingTaxType}
                    >
                        <DownloadIcon fontSize="small" />
                        {t('Download')}
                    </XtramButton>
                </>
            }
        >
            <div className={styles.content}>
                <section className={styles.controls}>
                    <ReportTypePicker
                        t={t}
                        types={REPORT_TYPES}
                        value={withHoldingTaxType}
                        onChange={setWithholdingTaxTypeFunc}
                    />
                    <div className={styles.controlsRow}>
                        <PeriodFields
                            t={t}
                            isDateRange={isDateRange}
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            date={date}
                            setDate={setDate}
                        />
                        <XtramButton
                            className={styles.viewBtn}
                            onClick={handleShowReport}
                            disabled={!withHoldingTaxType || isLoading}
                        >
                            <VisibilityIcon fontSize="small" />
                            {isLoading ? t('Loading') : t('ViewData')}
                        </XtramButton>
                    </div>
                </section>

                {htmlContent && (
                    <div className={styles.filters}>
                        <span className={styles.filtersLabel}>
                            <FilterAltIcon fontSize="small" />
                            {t('Filters')}
                        </span>
                        <div className={styles.filterField}>
                            <XtramSearch
                                direction={direction}
                                value={clientSearchTerm}
                                onChange={setClientSearchTerm}
                                placeholder={t('SearchClient')}
                            />
                        </div>
                        <XtramCheckbox
                            checked={showZeroBalanceCards}
                            onChange={handleZeroBalanceChange}
                            label={t('ShowZeroBalance')}
                        />
                        <XtramButton variant="ghost" small className={styles.clearBtn} onClick={handleClearFilters}>
                            <ClearAllIcon fontSize="small" />
                            {t('ClearFilters')}
                        </XtramButton>
                    </div>
                )}

                {showManualReporting && (
                    <section className={styles.manualCard}>
                        <h4 className={styles.manualTitle}>
                            {t('ManualReportingData')} · {selectedAccountCard.label}
                        </h4>
                        <div className={styles.controlsRow}>
                            <XtramField label={t('Amount')} className={styles.amountField}>
                                <XtramInput
                                    direction={direction}
                                    id="manualAmount"
                                    type="number"
                                    placeholder="0.00"
                                    inputProps={{ step: '0.01' }}
                                />
                            </XtramField>
                            <XtramField label={t('WithholdingTax')} className={styles.amountField}>
                                <XtramInput
                                    direction={direction}
                                    id="manualWithholding"
                                    type="number"
                                    placeholder="0.00"
                                    inputProps={{ step: '0.01' }}
                                />
                            </XtramField>
                            <XtramField label={t('Comment')} className={styles.commentField}>
                                <XtramInput direction={direction} id="manualComment" placeholder={t('EnterComment')} />
                            </XtramField>
                            <XtramButton onClick={handleSaveManualReportingData}>
                                <SaveIcon fontSize="small" />
                                {t('SaveReportingData')}
                            </XtramButton>
                        </div>
                    </section>
                )}

                <ReportPreview
                    t={t}
                    html={htmlContent}
                    loading={isLoading}
                    title="Client Withholding Report Preview"
                    sandbox="allow-same-origin allow-scripts"
                    onLoad={handleFrameLoad}
                />
            </div>
        </XtramModal>
    );
};

export default ClientWithholdingModal;
